<script setup>
import { computed, ref } from 'vue'
import { usePlaybackStore } from '@stores/playback'
import ShowOptions from '@components/show/show-options'
import formatTime from '@utils/format-time'
import formatTimeAMPM from '@utils/format-time-am-pm'
import formatDate from '@utils/format-date'

const props = defineProps({
    artwork: {
        type: Boolean,
        default: false,
    },
    playlistItem: {
        type: Object,
        default: () => {
            return {
                show: {
                    id: 'id',
                    title: 'showTitle',
                    url: 'showUrl',
                    type: 'show',
                    image: {},
                    genre: [],
                    artist_id: {},
                    artist: {},
                },
            }
        },
    },
})

const playbackStore = usePlaybackStore()

const isTileHovered = ref(false)

const mix = computed(() => props.playlistItem.mixes)

// A scheduled slot arrives without its `mixes` whenever the API cannot expand the
// reference, so nothing here may reach through it unguarded.
const show = computed(() => props.playlistItem.mixes?.show || false)

const airTime = computed(() => {
    const date = new Date(props.playlistItem.scheduled_start_time)
    return (props.artwork ? formatTimeAMPM(date) : formatTime(date)) || ''
})

const airDate = computed(() => {
    const date = new Date(props.playlistItem.scheduled_start_time)
    return formatDate(date) || ''
})

const playbackStatus = computed(
    () => playbackStore.status === 'playing' || playbackStore.status === 'loading'
)

const isMixLoaded = computed(
    () => playbackStore.resource && mix.value?.id === playbackStore.resource.id
)
</script>

<template>
    <div
        :class="[
            'show-schedule-vertical-tile',
            artwork && 'show-schedule-vertical-tile-artwork',
            { 'show-schedule-vertical-tile-active': isMixLoaded && playbackStatus },
        ]"
        @mouseover="isTileHovered = true"
        @mouseleave="isTileHovered = false"
    >
        <div class="date-time">
            <BaseLink
                v-if="mix && mix.id"
                class="time"
                :to="{ name: 'mix', params: { id: mix.id } }"
            >
                {{ airTime }}
            </BaseLink>
            <BasePlaceholder
                v-else
                :text="airTime"
                :size="3"
            />
            <div
                v-if="playlistItem.premiere"
                class="new"
            >New</div>
            <div
                v-if="artwork"
                class="date"
            >
                <BaseLink
                    v-if="mix && mix.id"
                    :to="{ name: 'mix', params: { id: mix.id } }"
                >
                    {{ airDate }}
                </BaseLink>
                <BasePlaceholder
                    v-else
                    :text="airDate"
                />
            </div>
        </div>

        <BaseResourceImage
            v-if="artwork"
            :resource="show"
            class="image"
            :playable-mix="mix"
            :has-hover-state="isTileHovered"
            radius="list"
        />
        <div
            v-if="mix"
            class="show"
        >
            <BaseLink
                v-if="mix.artist"
                :class="['title', 'show-link']"
                :to="{ path: `/artist/${mix.artist.url}/${mix.artist.id}` }"
            >
                {{ mix.artist.title }}
            </BaseLink>
            <BasePlaceholder
                v-else
                :class="['title', 'show-link']"
                :text="mix.artist ? mix.artist.title : ''"
                type="artist"
                tag="div"
            />
            <div :class="['subtitle', 'artist']">
                <BaseLink
                    v-if="mix.show && mix.show.title"
                    :to="{ name: 'show', params: { id: mix.show.id } }"
                >
                    {{ mix.show.title }}
                </BaseLink>
                <BasePlaceholder
                    v-else
                    type="show"
                />
            </div>
        </div>
        <div
            v-if="show"
            @click.prevent="() => {}"
        >
            <ShowOptions :show="show" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.show-schedule-vertical-tile {
    position: relative;
    display: flex;
    align-items: center;
    padding-top: $s-s;
    padding-bottom: $s-s;
    margin: 0;
    border-bottom: 1px solid $c-surface-raised;

    &:hover {
        background: $c-state-hover;
    }

    &.show-schedule-vertical-tile-active {
        background: $c-state-selected;
    }
}

.date-time {
    display: inline-block;
    width: 40px;
    min-height: 38px;

    .show-schedule-vertical-tile-artwork & {
        width: auto;

        @include desktop {
            width: span(1 of 12 narrow);
        }
    }
}

.time {
    display: inline-block;
    margin: 0 0 $s-xxs;
    font-size: 13px;
    font-weight: bold;
    line-height: 16px;
    vertical-align: top;
    letter-spacing: 0.5px;
    white-space: nowrap;

    &,
    &:visited {
        color: $c-white;
    }

    .show-schedule-vertical-tile-active & {
        color: $c-highlight;
    }
}

.show-schedule-vertical-tile:nth-of-type(1) {
    .time {
        color: $c-text;
    }
}

.new {
    max-width: 40px;
    padding: 2px 0;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;
    color: $c-accent-default;
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 0.17px;
    border: 2px solid $c-accent-default;

    .show-schedule-vertical-tile-artwork & {
        @include desktop {
            display: none;
        }
    }
}

.show {
    display: inline-block;
    width: 70%;
    padding-left: $s-l;
    margin-right: auto;
    vertical-align: top;

    &-link {
        display: block;

        &,
        &:visited {
            color: $c-white;
        }
    }

    .show-schedule-vertical-tile-artwork & {
        @include desktop {
            display: flex;
        }
    }

    .show-schedule-vertical-tile-artwork &-link {
        @include desktop {
            min-width: 55%;
        }
    }
}

.title {
    @extend %contentTitleM;

    max-width: 220px;
    margin: 0 0 $s-xxs;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: none;

    .show-schedule-vertical-tile-active & {
        color: $c-highlight;
    }
}

.artist {
    @extend %contentSubtitleM;

    max-width: 100%;
    height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
    white-space: nowrap;

    a {
        color: $c-text-secondary;
    }
}

@media (min-width: $desktop) {
    .time {
        color: $c-text;
    }
}

.image {
    display: none;
    width: 2rem;
    margin-left: gutter(of 12 narrow);
    pointer-events: auto;
    cursor: pointer;

    @include tablet {
        display: block;
    }
}

.date {
    display: none;

    @extend %contentSubtitleS;

    @include desktop {
        display: block;
        white-space: nowrap;
    }

    a {
        color: $c-text-secondary;
    }
}
</style>
